import React, { useState, memo } from 'react';
import {
  Marker,
  Polyline
} from '@react-google-maps/api';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import TowTruckIcon from '../../assets/images/tow.png';
import DigitalDispatchInfoWindow from './DigitalDispatchInfoWindow';

const DEFAULT_POLYLINE_OPTIONS = {
  strokeColor: '#FF9430',
  strokeOpacity: 0.8,
  strokeWeight: 6,
};

const useStyles = makeStyles((theme) => ({
  driverMarker: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    padding: theme.spacing(0.7),
    marginBottom: theme.spacing(5),
    borderRadius: theme.spacing(0.3)
  }
}));

function DigitalDispatchDriverMarker({
  driverRouteWaypoints,
  mostRecentDriverLocation,
  driverWaypointHistorySwitchState
}) {
  const classes = useStyles();
  const driverWaypointCoordinates = driverRouteWaypoints?.map(({ assignedVehicle }) => ({
    lat: assignedVehicle?.geoPoint?.latitude,
    lng: assignedVehicle?.geoPoint?.longitude
  }));
  const [driverWaypointHistoryInfoWindow, setDriverWaypointHistoryInfoWindow] = useState(null);

  /**
   * @returns {void}
   */
  const onCloseClickInfoWindow = () => setDriverWaypointHistoryInfoWindow(null);

  /**
   * @returns {void}
   */
  const onClickMarker = (currentWaypoint) => setDriverWaypointHistoryInfoWindow(currentWaypoint);

  return (
    <>
      {driverWaypointCoordinates && driverWaypointHistorySwitchState && (
        <Polyline
          path={driverWaypointCoordinates}
          options={DEFAULT_POLYLINE_OPTIONS}
        />
      )}
      {driverWaypointHistorySwitchState && driverRouteWaypoints?.map((currentWaypoint) => (
        currentWaypoint.id !== mostRecentDriverLocation?.id
        && (
          <Marker
            onClick={() => onClickMarker(currentWaypoint)}
            animation={window.google.maps.Animation.DROP}
            key={`${currentWaypoint.id}-location-update`}
            position={{
              lat: currentWaypoint?.assignedVehicle?.geoPoint?.latitude,
              lng: currentWaypoint?.assignedVehicle?.geoPoint?.longitude
            }}
            clickable
            draggable={false}
          >
            {driverWaypointHistoryInfoWindow?.id === currentWaypoint.id
              && (
                <DigitalDispatchInfoWindow
                  waypointData={driverWaypointHistoryInfoWindow}
                  onCloseClick={onCloseClickInfoWindow}
                />
              )}
          </Marker>
        )
      ))}
      {mostRecentDriverLocation
        && (
          <Marker
            onClick={() => onClickMarker(mostRecentDriverLocation)}
            icon={{
              url: TowTruckIcon,
              scaledSize: new window.google.maps.Size(40, 40),
            }}
            key={`${mostRecentDriverLocation.id}-location-update`}
            position={{
              lat: mostRecentDriverLocation?.assignedVehicle?.geoPoint.latitude,
              lng: mostRecentDriverLocation?.assignedVehicle?.geoPoint.longitude
            }}
            label={{
              text:
                'Driver Current Location',
              color: 'white',
              fontSize: '0.7rem',
              className: classes?.driverMarker
            }}
            clickable
            draggable={false}
          >
            {driverWaypointHistoryInfoWindow?.id === mostRecentDriverLocation.id
              && (
                <DigitalDispatchInfoWindow
                  waypointData={driverWaypointHistoryInfoWindow}
                  onCloseClick={onCloseClickInfoWindow}
                />
              )}
          </Marker>
        )}
    </>
  );
}

DigitalDispatchDriverMarker.propTypes = {
  driverRouteWaypoints: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    bsCompanyId: PropTypes.number,
    contractorId: PropTypes.string,
    eta: PropTypes.string,
    jobId: PropTypes.string,
    locationId: PropTypes.string,
    motorClubKey: PropTypes.string,
    phone: PropTypes.string,
    purchaseOrderId: PropTypes.string,
    referenceId: PropTypes.string,
    responseId: PropTypes.string,
    zip: PropTypes.string,
    dateCreated: PropTypes.string,
    dateModified: PropTypes.string,
    sourceType: PropTypes.string,
    assignedVehicle: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      geoPoint: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number
      }),
      gpsLastUpdate: PropTypes.string,
      direction: PropTypes.number
    }),
    assignedDriver: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string
    })
  })).isRequired,
  mostRecentDriverLocation: PropTypes.shape({
    id: PropTypes.number,
    bsCompanyId: PropTypes.number,
    contractorId: PropTypes.string,
    eta: PropTypes.string,
    jobId: PropTypes.string,
    locationId: PropTypes.string,
    motorClubKey: PropTypes.string,
    phone: PropTypes.string,
    purchaseOrderId: PropTypes.string,
    referenceId: PropTypes.string,
    responseId: PropTypes.string,
    zip: PropTypes.string,
    dateCreated: PropTypes.string,
    dateModified: PropTypes.string,
    sourceType: PropTypes.string,
    assignedVehicle: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      geoPoint: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number
      }),
      gpsLastUpdate: PropTypes.string,
      direction: PropTypes.number
    }),
    assignedDriver: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string
    })
  }),
  driverWaypointHistorySwitchState: PropTypes.bool
};

export default memo(DigitalDispatchDriverMarker);
